import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  Divider,
  Stack,
  Alert,
  CircularProgress,
  Badge,
  Stepper,
  Step,
  StepLabel,
  FormHelperText
} from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import moment from 'moment';

const PaymentUpload = () => {
  const [paymentType, setPaymentType] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(moment());
  const [reference, setReference] = useState('');
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  
  const { userUnit } = useSelector(state => state.auth);
  
  // 模擬加載付款歷史
  useEffect(() => {
    // 這裡會是API調用以獲取實際數據
    const mockHistory = [
      {
        id: 1,
        type: 'management_fee',
        amount: 1500,
        date: '2023-05-01',
        reference: 'MF-202305',
        status: 'verified',
        fileName: 'management_fee_receipt.pdf',
        fileType: 'application/pdf',
        uploadDate: '2023-05-02'
      },
      {
        id: 2,
        type: 'water_bill',
        amount: 320,
        date: '2023-05-15',
        reference: 'WB-202305',
        status: 'pending',
        fileName: 'water_bill_receipt.jpg',
        fileType: 'image/jpeg',
        uploadDate: '2023-05-16'
      },
      {
        id: 3,
        type: 'electricity_bill',
        amount: 780,
        date: '2023-04-20',
        reference: 'EB-202304',
        status: 'rejected',
        fileName: 'electricity_receipt.pdf',
        fileType: 'application/pdf',
        uploadDate: '2023-04-22',
        rejectionReason: '收據不清晰，請重新上傳'
      }
    ];
    
    setPaymentHistory(mockHistory);
  }, []);
  
  const steps = ['選擇付款類型', '輸入付款詳情', '上傳收據'];
  
  // 處理文件選擇
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    
    // 創建文件預覽URL
    const reader = new FileReader();
    reader.onload = () => {
      setFilePreview(reader.result);
    };
    reader.readAsDataURL(selectedFile);
    
    // 清除之前的錯誤
    if (errors.file) {
      setErrors({ ...errors, file: null });
    }
  };
  
  // 驗證當前步驟
  const validateStep = () => {
    const newErrors = {};
    
    if (activeStep === 0) {
      if (!paymentType) newErrors.paymentType = '請選擇付款類型';
    } else if (activeStep === 1) {
      if (!paymentAmount) newErrors.paymentAmount = '請輸入付款金額';
      else if (isNaN(paymentAmount) || parseFloat(paymentAmount) <= 0) {
        newErrors.paymentAmount = '請輸入有效的金額';
      }
      
      if (!paymentDate) newErrors.paymentDate = '請選擇付款日期';
      if (!reference.trim()) newErrors.reference = '請輸入參考編號';
    } else if (activeStep === 2) {
      if (!file) newErrors.file = '請上傳收據文件';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // 處理下一步
  const handleNext = () => {
    if (validateStep()) {
      if (activeStep === steps.length - 1) {
        handleSubmit();
      } else {
        setActiveStep(prevStep => prevStep + 1);
      }
    }
  };
  
  // 處理返回上一步
  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };
  
  // 處理提交
  const handleSubmit = () => {
    setUploading(true);
    
    // 模擬上傳過程
    setTimeout(() => {
      // 在實際應用中，這裡將是API調用
      
      // 添加到付款歷史
      const newPayment = {
        id: Date.now(),
        type: paymentType,
        amount: parseFloat(paymentAmount),
        date: paymentDate.format('YYYY-MM-DD'),
        reference,
        status: 'pending',
        fileName: file.name,
        fileType: file.type,
        uploadDate: moment().format('YYYY-MM-DD')
      };
      
      setPaymentHistory([newPayment, ...paymentHistory]);
      
      // 重置表單
      setPaymentType('');
      setPaymentAmount('');
      setPaymentDate(moment());
      setReference('');
      setFile(null);
      setFilePreview('');
      setActiveStep(0);
      setUploading(false);
      
      // 顯示成功消息
      setSuccessMessage('付款記錄已成功上傳！');
      setTimeout(() => setSuccessMessage(''), 5000);
    }, 2000);
  };
  
  // 處理付款預覽
  const handlePaymentPreview = (payment) => {
    setSelectedPayment(payment);
    setPreviewOpen(true);
  };
  
  // 格式化付款類型顯示
  const formatPaymentType = (type) => {
    switch (type) {
      case 'management_fee':
        return '管理費';
      case 'water_bill':
        return '水費';
      case 'electricity_bill':
        return '電費';
      case 'gas_bill':
        return '煤氣費';
      case 'rental':
        return '租金';
      default:
        return type;
    }
  };
  
  // 獲取狀態標籤
  const getStatusChip = (status) => {
    switch (status) {
      case 'pending':
        return <Chip size="small" label="待審核" color="primary" />;
      case 'verified':
        return <Chip size="small" label="已確認" color="success" icon={<CheckCircleIcon />} />;
      case 'rejected':
        return <Chip size="small" label="已拒絕" color="error" />;
      default:
        return <Chip size="small" label={status} />;
    }
  };
  
  return (
    <Box>
      <Typography variant="h5" gutterBottom>付款上傳</Typography>
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}
      
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {activeStep === 0 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                請選擇付款類型
              </Typography>
              <FormControl 
                fullWidth 
                margin="normal"
                error={!!errors.paymentType}
              >
                <InputLabel>付款類型</InputLabel>
                <Select
                  value={paymentType}
                  label="付款類型"
                  onChange={(e) => setPaymentType(e.target.value)}
                >
                  <MenuItem value="management_fee">管理費</MenuItem>
                  <MenuItem value="water_bill">水費</MenuItem>
                  <MenuItem value="electricity_bill">電費</MenuItem>
                  <MenuItem value="gas_bill">煤氣費</MenuItem>
                  <MenuItem value="rental">租金</MenuItem>
                  <MenuItem value="other">其他</MenuItem>
                </Select>
                {errors.paymentType && <FormHelperText>{errors.paymentType}</FormHelperText>}
              </FormControl>
            </Box>
          )}
          
          {activeStep === 1 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                輸入付款詳情
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="付款金額"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    fullWidth
                    margin="normal"
                    type="number"
                    InputProps={{
                      startAdornment: <AttachMoneyIcon color="action" sx={{ mr: 1 }} />,
                    }}
                    error={!!errors.paymentAmount}
                    helperText={errors.paymentAmount}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      label="付款日期"
                      value={paymentDate}
                      onChange={setPaymentDate}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          fullWidth 
                          margin="normal"
                          error={!!errors.paymentDate}
                          helperText={errors.paymentDate}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="參考編號/收據編號"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    fullWidth
                    margin="normal"
                    error={!!errors.reference}
                    helperText={errors.reference}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
          
          {activeStep === 2 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                上傳收據
              </Typography>
              
              <Box 
                sx={{ 
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  mb: 2,
                  bgcolor: 'background.paper',
                  ...(errors.file ? { borderColor: 'error.main' } : {})
                }}
              >
                <input
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  id="raised-button-file"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="raised-button-file">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                  >
                    選擇文件
                  </Button>
                </label>
                
                {errors.file && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {errors.file}
                  </Typography>
                )}
                
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  支持的格式：JPG, PNG, PDF (最大 10MB)
                </Typography>
              </Box>
              
              {file && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    已選擇文件: {file.name}
                  </Typography>
                  
                  {filePreview && file.type.startsWith('image/') ? (
                    <Box 
                      component="img" 
                      src={filePreview} 
                      sx={{ 
                        maxWidth: '100%', 
                        maxHeight: 200,
                        objectFit: 'contain',
                        mt: 1,
                        border: '1px solid #eee',
                        borderRadius: 1
                      }} 
                    />
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1 }}>
                      {file.type === 'application/pdf' ? (
                        <PictureAsPdfIcon color="error" sx={{ fontSize: 48 }} />
                      ) : (
                        <ImageIcon color="primary" sx={{ fontSize: 48 }} />
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
            <Button
              color="inherit"
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              返回
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={uploading}
            >
              {uploading ? (
                <CircularProgress size={24} />
              ) : activeStep === steps.length - 1 ? (
                '提交'
              ) : (
                '下一步'
              )}
            </Button>
          </Box>
        </CardContent>
      </Card>
      
      <Typography variant="h6" gutterBottom>
        付款歷史
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>類型</TableCell>
              <TableCell>金額</TableCell>
              <TableCell>日期</TableCell>
              <TableCell>參考編號</TableCell>
              <TableCell>狀態</TableCell>
              <TableCell>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paymentHistory.length > 0 ? (
              paymentHistory.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell>{formatPaymentType(payment.type)}</TableCell>
                  <TableCell>${payment.amount.toFixed(2)}</TableCell>
                  <TableCell>{moment(payment.date).format('YYYY/MM/DD')}</TableCell>
                  <TableCell>{payment.reference}</TableCell>
                  <TableCell>{getStatusChip(payment.status)}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handlePaymentPreview(payment)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  沒有付款記錄
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* 付款詳情對話框 */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth>
        {selectedPayment && (
          <>
            <DialogTitle>
              付款詳情
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    付款類型
                  </Typography>
                  <Typography variant="body1">
                    {formatPaymentType(selectedPayment.type)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    金額
                  </Typography>
                  <Typography variant="body1">
                    ${selectedPayment.amount.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    付款日期
                  </Typography>
                  <Typography variant="body1">
                    {moment(selectedPayment.date).format('YYYY/MM/DD')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    上傳日期
                  </Typography>
                  <Typography variant="body1">
                    {moment(selectedPayment.uploadDate).format('YYYY/MM/DD')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    參考編號
                  </Typography>
                  <Typography variant="body1">
                    {selectedPayment.reference}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    狀態
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {getStatusChip(selectedPayment.status)}
                  </Box>
                </Grid>
                
                {selectedPayment.status === 'rejected' && selectedPayment.rejectionReason && (
                  <Grid item xs={12}>
                    <Alert severity="error" sx={{ mt: 1 }}>
                      拒絕原因: {selectedPayment.rejectionReason}
                    </Alert>
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    收據文件
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    {selectedPayment.fileType.startsWith('image/') ? (
                      <ImageIcon color="primary" sx={{ mr: 1 }} />
                    ) : (
                      <PictureAsPdfIcon color="error" sx={{ mr: 1 }} />
                    )}
                    <Typography variant="body2">
                      {selectedPayment.fileName}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPreviewOpen(false)}>
                關閉
              </Button>
              {selectedPayment.status === 'rejected' && (
                <Button color="primary" onClick={() => {
                  setPreviewOpen(false);
                  setPaymentType(selectedPayment.type);
                  setPaymentAmount(selectedPayment.amount.toString());
                  setPaymentDate(moment(selectedPayment.date));
                  setReference(selectedPayment.reference);
                  setActiveStep(0);
                }}>
                  重新上傳
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default PaymentUpload; 